import { CommonModule } from '@angular/common';
import { Component, inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzCheckboxModule } from 'ng-zorro-antd/checkbox';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';

@Component({
  selector: 'app-save-layout',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NzButtonModule,
    NzCheckboxModule,
    NzFormModule,
    NzGridModule,
    NzInputModule,
    NzModalModule
  ],
  template: `
    <form nz-form [formGroup]="form" nzLayout="vertical">
      <nz-row [nzGutter]="16">
        <nz-col [nzSpan]="24">
          <nz-form-item>
            <nz-form-label [nzFor]="'layoutName'" nzRequired>Layout Name</nz-form-label>
            <nz-form-control [nzErrorTip]="layoutNameError">
              <input
                nz-input
                formControlName="layoutName"
                placeholder="Enter layout name"
                class="layout-input" />
            </nz-form-control>
          </nz-form-item>
        </nz-col>
        <ng-template #layoutNameError>
          <ng-container *ngIf="form.controls['layoutName'].hasError('required')">
            Layout Name is required.
          </ng-container>
          <ng-container *ngIf="form.controls['layoutName'].hasError('maxlength')">
            Layout Name cannot exceed 100 characters.
          </ng-container>
        </ng-template>

        <nz-col [nzSpan]="24">
          <nz-form-item>
            <nz-form-control>
              <label nz-checkbox formControlName="isDefault" class="default-checkbox">
                Save as default layout
              </label>
            </nz-form-control>
          </nz-form-item>
        </nz-col>
      </nz-row>

      <nz-row nzJustify="end" class="mt-md">
        <nz-col>
          <button nz-button nzType="default" (click)="cancel()" class="mr-sm">Cancel</button>
          <button
            nz-button
            nzType="primary"
            [disabled]="!form.valid || loading"
            [nzLoading]="loading"
            (click)="save()">
            {{ isEditing ? 'Update' : 'Save' }}
          </button>
        </nz-col>
      </nz-row>
    </form>
  `,
  styles: [`
    :host {
      display: block;
    }
    .mt-md {
      margin-top: 24px;
    }
    .mr-sm {
      margin-right: 8px;
    }
    ::ng-deep .ant-modal-header {
      border-radius: 1px 1px 0 0;
      background-color: #08306B;
      border-bottom: 1px solid #08306B;
    }
    ::ng-deep .ant-modal-title {
      color: #fff !important;
    }
    ::ng-deep .ant-modal-close {
      color: #fff !important;
    }
    ::ng-deep .ant-modal-close:hover {
      color: rgba(255, 255, 255, 0.85);
    }
    ::ng-deep .ant-modal-content {
      border-radius: 2px;
    }
    ::ng-deep .ant-modal-body {
      padding: 24px;
    }
    ::ng-deep .ant-form-item {
      margin-bottom: 16px;
    }
    ::ng-deep .ant-form-item-label {
      padding-bottom: 4px;
    }
    ::ng-deep .ant-form-item-label > label {
      font-weight: 500;
      color: rgba(0, 0, 0, 0.85);
    }
    .layout-input {
      border-radius: 2px;
    }
    .default-checkbox {
      font-size: 14px;
      color: rgba(0, 0, 0, 0.85);
    }
    ::ng-deep .ant-checkbox-wrapper + .ant-checkbox-wrapper {
      margin-left: 0;
    }
    ::ng-deep .ant-btn {
      border-radius: 2px;
      height: 32px;
      padding: 4px 16px;
      font-size: 14px;
    }

  `]
})
export class SaveLayoutComponent implements OnInit {
  private modal = inject(NzModalRef);
  private fb = inject(FormBuilder);

  visible = true;
  loading = false;
  isEditing = false;

  form: FormGroup = this.fb.group({
    layoutName: ['', [Validators.required, Validators.maxLength(100)]],
    isDefault: [false]
  });

  ngOnInit(): void {
    const config = this.modal.getConfig();
    if (config.nzData) {
      this.form.patchValue({
        layoutName: config.nzData.layoutName || '',
        isDefault: config.nzData.isDefault || false
      });
      this.isEditing = !!config.nzData.layoutName;
    }
  }

  cancel(): void {
    this.visible = false;
    setTimeout(() => {
      this.modal.close(null);
    }, 200);
  }

  save(): void {
    if (this.form.valid) {
      this.modal.close({
        name: this.form.value.layoutName,
        isDefault: this.form.value.isDefault
      });
    }
  }
}
